import { useMemo } from 'react';
import { FileEdit, AlertCircle, XCircle, Clock, Eye } from 'lucide-react';

import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableProps } from '@/shared/ui/data-table';
import {
  ChangePriorityDialog,
  PlaceOnHoldDialog,
  CancelConsignmentDialog,
  useConsignmentActions
} from '@/features/consignment-actions';
import { ShowDispositionDialog } from '@/features/show-disposition';
import type { ConsignmentTableData } from '@/pages/consignments/model';

interface ConsignmentsDataTableProps extends Omit<DataTableProps<ConsignmentTableData>, 'actions' | 'onRowAction'> {
  onClearSelection?: () => void;
}

export function ConsignmentsDataTable({
  selectedIds = [],
  onSelectionChange,
  onClearSelection,
  ...dataTableProps
}: ConsignmentsDataTableProps) {
  const { t } = useTranslation();
  const { actions, dialogs } = useConsignmentActions({
    selectedIds,
    onClearSelection
  });

  const rowActions = useMemo(() => {
    return [
      {
        id: 'show-disposition',
        label: t('listaction.RQ.DIS'),
        icon: Eye,
        onClick: (row: ConsignmentTableData) => actions.showDisposition([row.id])
      },
      {
        id: 'edit',
        label: t('listaction.RQ.EDT'),
        icon: FileEdit,
        onClick: (row: ConsignmentTableData) => actions.editView([row.id])
      },
      {
        id: 'priority',
        label: t('listaction.RQ.PRT'),
        icon: AlertCircle,
        onClick: (row: ConsignmentTableData) => actions.changePriority([row.id])
      },

      {
        id: 'hold',
        label: t('listaction.RQ.HOL'),
        icon: Clock,
        onClick: (row: ConsignmentTableData) => actions.placeOnHold([row.id])
      },
      {
        id: 'cancel',
        label: t('listaction.RQ.CAN'),
        icon: XCircle,
        onClick: (row: ConsignmentTableData) => actions.cancel([row.id])
      }
    ];
  }, [actions, t]);

  return (
    <>
      <DataTable
        {...dataTableProps}
        selectedIds={selectedIds}
        onSelectionChange={onSelectionChange}
        actions={rowActions}
      />

      <ChangePriorityDialog
        open={dialogs.changePriority.open}
        onOpenChange={dialogs.changePriority.onOpenChange}
        onConfirm={(priority, comments) => dialogs.changePriority.onConfirm(priority, comments)}
      />

      <PlaceOnHoldDialog
        open={dialogs.placeOnHold.open}
        onOpenChange={dialogs.placeOnHold.onOpenChange}
        onConfirm={(remarks) => dialogs.placeOnHold.onConfirm(remarks)}
      />

      <CancelConsignmentDialog
        open={dialogs.cancel.open}
        onOpenChange={dialogs.cancel.onOpenChange}
        onConfirm={(reason, remarks) => dialogs.cancel.onConfirm(reason, remarks)}
      />

      <ShowDispositionDialog
        open={dialogs.showDisposition.open}
        onOpenChange={dialogs.showDisposition.onOpenChange}
        consignmentId={dialogs.showDisposition.consignmentId}
      />
    </>
  );
}
